/**
 * WorksList コンポーネント (SC-02)
 *
 * 作品一覧ページ。全作品をグリッド表示し、検索・フィルタ・ソート機能を提供。
 * 検索ボックスの下に全8ジャンルを横並びラベルで常時表示。
 *
 */

import { useEffect, useMemo, useCallback } from 'react';
import { Header } from '@/components/organisms/Header';
import { Footer } from '@/components/organisms/Footer';
import { WorkCard } from '@/components/organisms/WorkCard';
import { FilterPanel } from '@/components/organisms/FilterPanel';
import { SearchBar } from '@/components/atoms/SearchBar';
import { useLanguageStore } from '@/stores/language-store';
import { useDataStore } from '@/stores/data-store';
import { useListPage } from '@/hooks/useListPage';
import { useI18n } from '@/i18n/useI18n';
import { createWorksSearchIndex, searchWorks } from '@/lib/search-engine';
import {
  buildFilterQueryParams,
  parseFilterQueryParams,
} from '@/lib/url-filter-helpers';
import {
  filterWorks,
  extractGenresFromWorks,
  extractArtformsFromWorks,
  extractKeywordsFromWorks,
  extractGroupsFromWorks,
  KEYWORD_FILTER_ALLOWLIST,
  getDateRange,
} from '@/lib/data-filters';
import { compareDates } from '@/lib/date-formatters';
import { cn } from '@/lib/cn';
import { Button } from '@/components/atoms/Button';
import { getGenreBackgroundClass } from '@/lib/genre-colors';

/**
 * WorksList コンポーネント
 *
 * 使用例:
 * ```tsx
 * <WorksList />
 * ```
 */
export function WorksList() {
  const { t } = useI18n();
  const { language } = useLanguageStore();
  const {
    works,
    isLoadingWorks: isLoading,
    worksLoaded,
    worksError,
    fetchWorks,
  } = useDataStore();

  // データ読み込み
  useEffect(() => {
    if (!worksLoaded && !worksError) fetchWorks();
  }, [worksLoaded, worksError, fetchWorks]);

  // 利用可能なフィルタオプションを抽出
  const availableGenres = useMemo(() => extractGenresFromWorks(works), [works]);
  const availableArtforms = useMemo(
    () => extractArtformsFromWorks(works),
    [works]
  );
  const availableKeywords = useMemo(
    () =>
      extractKeywordsFromWorks(works).filter((keyword) =>
        KEYWORD_FILTER_ALLOWLIST.has(keyword)
      ),
    [works]
  );
  const availableGroups = useMemo(() => extractGroupsFromWorks(works), [works]);
  const dateRangeLimit = useMemo(() => getDateRange(works, []), [works]);

  // URL パラメータ解析・構築関数（安定した参照を保つため useCallback でメモ化）
  const parseFilters = useCallback(
    (params: URLSearchParams) => {
      const parsed = parseFilterQueryParams(params);
      return {
        genres: parsed.genres ?? [],
        artforms: parsed.artforms ?? [],
        keywords: parsed.keywords ?? [],
        groups: parsed.groups ?? [],
        dateRange: parsed.dateRange ?? dateRangeLimit,
        searchQuery: parsed.searchQuery ?? '',
      };
    },
    [dateRangeLimit]
  );

  const buildParams = useCallback(
    (nextFilters: ReturnType<typeof parseFilters>) => {
      const isDefaultDateRange =
        nextFilters.dateRange[0] === dateRangeLimit[0] &&
        nextFilters.dateRange[1] === dateRangeLimit[1];
      const queryString = buildFilterQueryParams({
        genres: nextFilters.genres.length > 0 ? nextFilters.genres : undefined,
        artforms:
          nextFilters.artforms.length > 0 ? nextFilters.artforms : undefined,
        keywords:
          nextFilters.keywords.length > 0 ? nextFilters.keywords : undefined,
        groups: nextFilters.groups.length > 0 ? nextFilters.groups : undefined,
        dateRange: isDefaultDateRange ? undefined : nextFilters.dateRange,
        searchQuery: nextFilters.searchQuery.trim() || undefined,
      });
      return new URLSearchParams(queryString);
    },
    [dateRangeLimit]
  );

  // 共通リストページ hook
  const {
    filters,
    updateFilters,
    localSearchQuery,
    setLocalSearchQuery,
    isFilterOpen,
    setIsFilterOpen,
  } = useListPage({ parseFilters, buildParams });

  // 検索インデックスの作成（モジュールレベルでキャッシュ済み）
  const searchIndex = useMemo(
    () => (works.length > 0 ? createWorksSearchIndex(works, language) : null),
    [works, language]
  );

  // 依存配列をプリミティブに変換（参照変更による不要な再計算を防止）
  const genresKey = filters.genres.join(',');
  const artformsKey = filters.artforms.join(',');
  const keywordsKey = filters.keywords.join(',');
  const groupsKey = filters.groups.join(',');
  const dateRangeKey = `${filters.dateRange[0]}-${filters.dateRange[1]}`;

  // フィルタと検索を適用
  const filteredWorks = useMemo(() => {
    const filtered = filterWorks(
      works,
      {
        genres: filters.genres,
        artforms: filters.artforms,
        keywords: filters.keywords,
        groups: filters.groups,
        dateRange: filters.dateRange,
        searchQuery: filters.searchQuery,
      },
      language
    );

    const results =
      filters.searchQuery.trim() && searchIndex
        ? searchWorks(filters.searchQuery, searchIndex, filtered)
        : filtered;

    return [...results].sort((a, b) =>
      compareDates(a.dateCreated || '', b.dateCreated || '')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    works,
    genresKey,
    artformsKey,
    keywordsKey,
    groupsKey,
    dateRangeKey,
    filters.searchQuery,
    searchIndex,
    language,
  ]);

  // アクティブなフィルタ数をカウント（ジャンルは常時表示のためカウント対象外）
  const activeFilterCount =
    filters.artforms.length +
    filters.keywords.length +
    filters.groups.length +
    (filters.dateRange[0] !== dateRangeLimit[0] ||
    filters.dateRange[1] !== dateRangeLimit[1]
      ? 1
      : 0);

  // ジャンルラベルのトグル
  const handleGenreToggle = (genre: string) => {
    const nextGenres = filters.genres.includes(genre)
      ? filters.genres.filter((g) => g !== genre)
      : [...filters.genres, genre];

    updateFilters({ ...filters, genres: nextGenres });
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      <main id="main-content" className="flex-1">
        {/* ページタイトル */}
        <section className="border-b bg-muted/20 py-8">
          <div className="container mx-auto px-4">
            <h1 className="text-balance text-3xl font-bold">
              {t('worksList.title')}
            </h1>
            <p
              className="mt-2 text-sm text-muted-foreground"
              data-testid="works-count"
              aria-live="polite"
            >
              {t('worksList.showing', { count: filteredWorks.length })}
            </p>
          </div>
        </section>

        {/* ツールバー */}
        <section className="border-b bg-background py-4">
          <div className="container mx-auto px-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              {/* 検索バー */}
              <div className="flex-1">
                <SearchBar
                  placeholder={t('worksList.searchPlaceholder')}
                  value={localSearchQuery}
                  onChange={setLocalSearchQuery}
                />
              </div>

              {/* フィルタボタン */}
              <Button
                variant="outline"
                onClick={() => setIsFilterOpen(true)}
                className="relative shrink-0"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                {t('common.filter')}
                {activeFilterCount > 0 ? (
                  <span className="ml-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                    {activeFilterCount}
                  </span>
                ) : null}
              </Button>
            </div>

            {/* ジャンルラベル（常時表示） */}
            <div
              className="mt-4"
              role="group"
              aria-label={t('filter.genreFilterGroup')}
            >
              <div className="flex flex-wrap gap-2">
                {availableGenres.map((genre) => (
                  <button
                    key={genre}
                    onClick={() => handleGenreToggle(genre)}
                    aria-pressed={filters.genres.includes(genre)}
                    className={cn(
                      'rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
                      getGenreBackgroundClass(
                        genre,
                        filters.genres.includes(genre)
                      )
                    )}
                  >
                    {genre}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 作品グリッド */}
        <section className="py-8">
          <div className="container mx-auto px-4">
            {worksError ? (
              <div className="flex min-h-[400px] items-center justify-center">
                <div className="text-center">
                  <p className="text-lg font-medium">{t('common.loadError')}</p>
                  <button
                    onClick={() => fetchWorks()}
                    className="mt-4 inline-block text-sm text-primary hover:underline"
                  >
                    {t('common.retry')}
                  </button>
                </div>
              </div>
            ) : !worksLoaded || isLoading ? (
              <div className="flex min-h-[400px] items-center justify-center">
                <div className="text-center">
                  <div className="mb-4 text-4xl">⏳</div>
                  <p className="text-muted-foreground">{t('common.loading')}</p>
                </div>
              </div>
            ) : filteredWorks.length === 0 ? (
              <div className="flex min-h-[400px] items-center justify-center">
                <div className="text-center">
                  <div className="mb-4 text-4xl">🔍</div>
                  <p className="text-lg font-medium">
                    {t('worksList.noWorksFound')}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t('worksList.tryAdjusting')}
                  </p>
                </div>
              </div>
            ) : (
              <div
                role="list"
                className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              >
                {filteredWorks.map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      {/* フィルタパネル */}
      <FilterPanel
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        value={{
          genres: filters.genres,
          artforms: filters.artforms,
          keywords: filters.keywords,
          groups: filters.groups,
          dateRange: filters.dateRange,
        }}
        onApply={(next) => {
          updateFilters({
            ...filters,
            genres: next.genres,
            artforms: next.artforms,
            keywords: next.keywords,
            groups: next.groups,
            dateRange: next.dateRange,
          });
        }}
        onReset={() => {
          setLocalSearchQuery('');
          updateFilters({
            genres: [],
            artforms: [],
            keywords: [],
            groups: [],
            dateRange: dateRangeLimit,
            searchQuery: '',
          });
        }}
        availableGenres={availableGenres}
        availableArtforms={availableArtforms}
        availableKeywords={availableKeywords}
        availableGroups={availableGroups}
        dateRangeLimit={dateRangeLimit}
      />
    </div>
  );
}
